import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import SearchableSelect from '../ui/searchable-select';
import { X } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const AddTaskModal = ({ isOpen, onClose, onSubmit, editingTask }) => {
  const { getAccessToken } = useAuth();
  const [loading, setLoading] = useState(false);
  const [tutors, setTutors] = useState([]);
  const [students, setStudents] = useState([]);
  const [parents, setParents] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [formData, setFormData] = useState({
    tutorId: '',
    studentId: '',
    parentId: '',
    subject: '',
    title: '',
    billingRate: '',
    payoutRate: '',
    isGroupClass: false,
    groupClassBillingRate: '',
    groupClassPayoutRate: '',
    isOddHour: false,
    oddHourCharge: '',
    oddHourPayout: ''
  });

  // Fetch tutors and students
  useEffect(() => {
    if (isOpen) {
      fetchUsers();
    }
  }, [isOpen]);

  // Load editing task data when modal opens for editing
  useEffect(() => {
    if (editingTask) {
      setFormData({
        tutorId: editingTask.tutor_id || editingTask.tutorId || '',
        studentId: editingTask.student_id || editingTask.studentId || '',
        parentId: editingTask.parent_id || editingTask.parentId || 'none',
        subject: editingTask.subject || '',
        title: editingTask.title || editingTask.subject || '',
        billingRate: editingTask.billing_rate?.toString() || editingTask.billingRate?.toString() || '',
        payoutRate: editingTask.payout_rate?.toString() || editingTask.payoutRate?.toString() || '',
        isGroupClass: editingTask.is_group_class || editingTask.isGroupClass || false,
        groupClassBillingRate: editingTask.group_class_billing_rate?.toString() || editingTask.groupClassBillingRate?.toString() || '',
        groupClassPayoutRate: editingTask.group_class_payout_rate?.toString() || editingTask.groupClassPayoutRate?.toString() || '',
        isOddHour: editingTask.is_odd_hour || editingTask.isOddHour || false,
        oddHourCharge: editingTask.odd_hour_charge?.toString() || editingTask.oddHourCharge?.toString() || '',
        oddHourPayout: editingTask.odd_hour_payout?.toString() || editingTask.oddHourPayout?.toString() || ''
      });
    } else {
      setFormData({ tutorId: '', studentId: '', parentId: 'none', subject: '', title: '', billingRate: '', payoutRate: '', isGroupClass: false, groupClassBillingRate: '', groupClassPayoutRate: '', isOddHour: false, oddHourCharge: '', oddHourPayout: '' });
    }
  }, [editingTask, isOpen]);

  const fetchUsers = async () => {
    try {
      const token = getAccessToken();
      if (!token) return;

      console.log('🔍 AddTaskModal: Fetching registered users...');

      // Fetch registered tutors only
      const tutorUrl = `${process.env.REACT_APP_BACKEND_URL}/api/users/admin/users?role=tutor&is_registered=true&page_size=100`;
      console.log('📞 Fetching tutors from:', tutorUrl);
      const tutorsResponse = await fetch(
        tutorUrl,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      if (tutorsResponse.ok) {
        const tutorsResult = await tutorsResponse.json();
        const tutorsData = tutorsResult.users || [];
        console.log('✅ Fetched registered tutors:', tutorsData.length, 'tutors');
        setTutors(tutorsData);
      } else {
        const errorText = await tutorsResponse.text();
        console.error('❌ Failed to fetch tutors:', tutorsResponse.status, errorText);
      }

      // Fetch registered students only
      const studentUrl = `${process.env.REACT_APP_BACKEND_URL}/api/users/admin/users?role=student&is_registered=true&page_size=100`;
      console.log('📞 Fetching students from:', studentUrl);
      const studentsResponse = await fetch(
        studentUrl,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      if (studentsResponse.ok) {
        const studentsResult = await studentsResponse.json();
        const studentsData = studentsResult.users || [];
        console.log('✅ Fetched registered students:', studentsData.length, 'students');
        setStudents(studentsData);
      } else {
        const errorText = await studentsResponse.text();
        console.error('❌ Failed to fetch students:', studentsResponse.status, errorText);
      }

      // Fetch registered parents only
      const parentUrl = `${process.env.REACT_APP_BACKEND_URL}/api/users/admin/users?role=parent&is_registered=true&page_size=100`;
      console.log('📞 Fetching parents from:', parentUrl);
      const parentsResponse = await fetch(
        parentUrl,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      if (parentsResponse.ok) {
        const parentsResult = await parentsResponse.json();
        const parentsData = parentsResult.users || [];
        console.log('✅ Fetched registered parents:', parentsData.length, 'parents');
        setParents(parentsData);
      } else {
        const errorText = await parentsResponse.text();
        console.error('❌ Failed to fetch parents:', parentsResponse.status, errorText);
      }

      // Fetch subjects
      const subjectsResponse = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/subjects`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      if (subjectsResponse.ok) {
        const subjectsData = await subjectsResponse.json();
        setSubjects(subjectsData);
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = getAccessToken();
      if (!token) {
        alert('Please log in to create/update tasks');
        return;
      }

      const taskPayload = {
        title: formData.title || formData.subject,
        subject: formData.subject,
        tutor_id: formData.tutorId,
        student_id: formData.studentId,
        parent_id: formData.parentId || null,
        billing_rate: parseFloat(formData.billingRate),
        payout_rate: parseFloat(formData.payoutRate),
        is_group_class: formData.isGroupClass,
        group_class_billing_rate: formData.isGroupClass && formData.groupClassBillingRate ? parseFloat(formData.groupClassBillingRate) : null,
        group_class_payout_rate: formData.isGroupClass && formData.groupClassPayoutRate ? parseFloat(formData.groupClassPayoutRate) : null,
        is_odd_hour: formData.isOddHour,
        odd_hour_charge: formData.isOddHour && formData.oddHourCharge ? parseFloat(formData.oddHourCharge) : null,
        odd_hour_payout: formData.isOddHour && formData.oddHourPayout ? parseFloat(formData.oddHourPayout) : null
      };

      let response;
      if (editingTask) {
        // Update existing task
        response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/tasks/${editingTask.id}`,
          {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(taskPayload)
          }
        );
      } else {
        // Create new task
        response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/tasks`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(taskPayload)
          }
        );
      }

      if (response.ok) {
        const createdTask = await response.json();
        onSubmit(createdTask);
        setFormData({ tutorId: '', studentId: '', subject: '', title: '', billingRate: '', payoutRate: '', isGroupClass: false, groupClassBillingRate: '', groupClassPayoutRate: '', isOddHour: false, oddHourCharge: '', oddHourPayout: '' });
        alert(editingTask ? 'Task updated successfully!' : 'Task created successfully!');
      } else {
        const error = await response.json();
        alert(`Failed to ${editingTask ? 'update' : 'create'} task: ${error.detail}`);
      }
    } catch (error) {
      console.error('Failed to submit task:', error);
      alert('Failed to submit task');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editingTask ? 'Edit Task' : 'Create New Task'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3 mt-3">
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Task Name *</label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData({...formData, title: e.target.value})}
              placeholder="e.g., Math Tutoring for Grade 10"
              required
            />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Select Tutor * <span className="text-xs text-gray-500">(Registered only)</span>
              </label>
              <SearchableSelect
                value={formData.tutorId}
                onValueChange={(value) => setFormData({...formData, tutorId: value})}
                options={tutors.map(tutor => ({
                  value: tutor.id,
                  label: tutor.full_name,
                  email: tutor.email
                }))}
                placeholder="Search and select tutor"
                searchPlaceholder="Type to search tutors..."
                emptyMessage="No registered tutors found"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Select Student * <span className="text-xs text-gray-500">(Registered only)</span>
              </label>
              <SearchableSelect
                value={formData.studentId}
                onValueChange={(value) => setFormData({...formData, studentId: value})}
                options={students.map(student => ({
                  value: student.id,
                  label: student.full_name,
                  email: student.email
                }))}
                placeholder="Search and select student"
                searchPlaceholder="Type to search students..."
                emptyMessage="No registered students found"
                required
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Select Parent (Optional) <span className="text-xs text-gray-500">(Registered only)</span>
              </label>
              <SearchableSelect
                value={formData.parentId || 'none'}
                onValueChange={(value) => setFormData({...formData, parentId: value === 'none' ? '' : value})}
                options={[
                  { value: 'none', label: 'None', email: '' },
                  ...parents.map(parent => ({
                    value: parent.id,
                    label: parent.full_name,
                    email: parent.email
                  }))
                ]}
                placeholder="Search and select parent (optional)"
                searchPlaceholder="Type to search parents..."
                emptyMessage="No registered parents found"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Subject *</label>
              {subjects.length > 0 ? (
                <Select value={formData.subject} onValueChange={(value) => setFormData({...formData, subject: value})} required>
                  <SelectTrigger>
                    <SelectValue placeholder="Select subject" />
                  </SelectTrigger>
                  <SelectContent>
                    {subjects.map((subject) => (
                      <SelectItem key={subject.id} value={subject.name}>
                        {subject.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  value={formData.subject}
                  onChange={(e) => setFormData({...formData, subject: e.target.value})}
                  placeholder="e.g., Mathematics, Physics"
                  required
                />
              )}
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Billing Rate (₹/hour) *</label>
              <Input
                type="number"
                value={formData.billingRate}
                onChange={(e) => setFormData({...formData, billingRate: e.target.value})}
                placeholder="600"
                required
                min="0"
                step="any"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Payout Rate (₹/hour) *</label>
              <Input
                type="number"
                value={formData.payoutRate}
                onChange={(e) => setFormData({...formData, payoutRate: e.target.value})}
                placeholder="400"
                required
                min="0"
                step="any"
              />
            </div>
          </div>

          <div className="bg-cyan-50 dark:bg-gray-700 border border-cyan-200 dark:border-cyan-800 rounded-lg p-3">
            <p className="text-xs text-gray-700 dark:text-gray-300">
              <span className="font-semibold">Note:</span> Billing rate is what parents pay. Payout rate is what tutors earn.
            </p>
          </div>

          <div className="border-t border-gray-200 dark:border-gray-700 pt-3">
            <div className="flex items-center space-x-2 mb-3">
              <Checkbox
                id="groupClassCheckbox"
                checked={formData.isGroupClass}
                onCheckedChange={(checked) => setFormData({...formData, isGroupClass: checked})}
              />
              <label htmlFor="groupClassCheckbox" className="text-sm font-semibold text-gray-700 dark:text-gray-300 cursor-pointer">
                Enable Group Class
              </label>
            </div>

            {formData.isGroupClass && (
              <div className="grid md:grid-cols-2 gap-3 bg-purple-50 dark:bg-purple-900/20 p-3 rounded-lg border border-purple-200 dark:border-purple-800">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Group Class Billing Rate (₹/hour) *</label>
                  <Input
                    type="number"
                    value={formData.groupClassBillingRate}
                    onChange={(e) => setFormData({...formData, groupClassBillingRate: e.target.value})}
                    placeholder="900"
                    required={formData.isGroupClass}
                    min="0"
                    step="any"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Group Class Payout Rate (₹/hour) *</label>
                  <Input
                    type="number"
                    value={formData.groupClassPayoutRate}
                    onChange={(e) => setFormData({...formData, groupClassPayoutRate: e.target.value})}
                    placeholder="600"
                    required={formData.isGroupClass}
                    min="0"
                    step="any"
                  />
                </div>

                <div className="col-span-2 -mb-1">
                  <p className="text-xs text-purple-700 dark:text-purple-300">
                    Group class rates apply when tutor marks timesheet as group class.
                  </p>
                </div>
              </div>
            )}
          </div>

          <div className="border-t border-gray-200 dark:border-gray-700 pt-3">
            <div className="flex items-center space-x-2 mb-3">
              <Checkbox
                id="oddHourCheckbox"
                checked={formData.isOddHour}
                onCheckedChange={(checked) => setFormData({...formData, isOddHour: checked})}
              />
              <label htmlFor="oddHourCheckbox" className="text-sm font-semibold text-gray-700 dark:text-gray-300 cursor-pointer">
                Enable Odd Hour
              </label>
            </div>

            {formData.isOddHour && (
              <div className="grid md:grid-cols-2 gap-3 bg-amber-50 dark:bg-amber-900/20 p-3 rounded-lg border border-amber-200 dark:border-amber-800">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Odd Hour Charge (₹) *</label>
                  <Input
                    type="number"
                    value={formData.oddHourCharge}
                    onChange={(e) => setFormData({...formData, oddHourCharge: e.target.value})}
                    placeholder="200"
                    required={formData.isOddHour}
                    min="0"
                    step="any"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Odd Hour Payout (₹) *</label>
                  <Input
                    type="number"
                    value={formData.oddHourPayout}
                    onChange={(e) => setFormData({...formData, oddHourPayout: e.target.value})}
                    placeholder="150"
                    required={formData.isOddHour}
                    min="0"
                    step="any"
                  />
                </div>

                <div className="col-span-2 -mb-1">
                  <p className="text-xs text-amber-700 dark:text-amber-300">
                    Odd hour charges are added to base amounts (not multiplied).
                  </p>
                </div>
              </div>
            )}
          </div>

          <div className="flex gap-3 justify-end pt-3 sticky bottom-0 bg-white dark:bg-gray-800 -mx-6 px-6 py-3 border-t border-gray-200 dark:border-gray-700">
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>Cancel</Button>
            <Button type="submit" className="bg-cyan-600 hover:bg-cyan-700 text-white" disabled={loading}>
              {loading ? 'Saving...' : (editingTask ? 'Update Task' : 'Create Task')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddTaskModal;
